import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

export const ChangePasswordDialog = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error(t("passwordTooShort"));
    if (next !== confirm) return toast.error(t("passwordMismatch"));
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("passwordChanged"));
      setOpen(false);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="change-password-btn"
          variant="ghost"
          size="sm"
          className="text-slate-700"
          title={t("changePassword")}
        >
          <KeyRound className="w-4 h-4 me-1.5" />
          <span className="hidden sm:inline">{t("changePassword")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("changePassword")}</DialogTitle>
          <DialogDescription className="sr-only">{t("changePassword")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-sm">{t("currentPassword")}</Label>
            <Input
              data-testid="current-password-input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="text-sm">{t("newPassword")}</Label>
            <Input
              data-testid="new-password-input"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="text-sm">{t("confirmNewPassword")}</Label>
            <Input
              data-testid="confirm-password-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-1.5 h-11"
            />
          </div>
          <Button
            data-testid="save-password-btn"
            type="submit"
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 rounded-full h-11"
          >
            {saving ? "…" : t("save")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
